import { Box, styled, Typography, useTheme } from '@mui/material';
import { IconSectionClosed, IconSectionOpen, IconWarning } from 'assets/img';
import { useToggle } from 'shared/hooks';

type Props = {
  title: string;
  warningText?: string;
  initializeAsOpen?: boolean;
  children: React.ReactNode;
};

export const CollapsibleSection = ({ title, warningText, initializeAsOpen, children }: Props) => {
  const { on, toggle } = useToggle(initializeAsOpen ?? true);
  const theme = useTheme();

  return (
    <Box>
      <SectionHeader onClick={toggle}>
        <SectionTitle>
          <SectionStateIcon>
            {on ? <StyledIconSectionOpen /> : <StyledIconSectionClosed />}
          </SectionStateIcon>
          <Typography variant="h2" style={{ color: theme.palette.lwSecondary[100] }}>
            {title}
          </Typography>
        </SectionTitle>
        {warningText ? (
          <SectionWarning>
            <IconWarning stroke={theme.palette.lwDanger[100]} />
            <SectionWarningText
              variant="body1"
              style={{ color: theme.palette.lwDanger[100], fontStyle: 'italic' }}
            >
              {warningText}
            </SectionWarningText>
          </SectionWarning>
        ) : null}
      </SectionHeader>

      {on ? <SectionContent>{children}</SectionContent> : null}
    </Box>
  );
};

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(5),
  backgroundColor: theme.palette.lwPrimary[10],
  borderRadius: theme.spacing(4),
  cursor: 'pointer',
}));

const SectionStateIcon = styled(Box)(({ theme }) => ({
  width: theme.spacing(4),
  height: theme.spacing(4),
}));

const SectionTitle = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(4),
}));

const StyledIconSectionOpen = styled(IconSectionOpen)(({ theme }) => ({
  '& path': {
    fill: theme.palette.lwSecondary[100],
  },
}));

const StyledIconSectionClosed = styled(IconSectionClosed)(({ theme }) => ({
  '& path': {
    fill: theme.palette.lwSecondary[100],
  },
}));

const SectionWarning = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginLeft: theme.spacing(4),
}));

const SectionWarningText = styled(Typography)(({ theme }) => ({
  color: theme.palette.lwDanger[100],
  fontStyle: 'italic',
}));

const SectionContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  paddingBottom: theme.spacing(0),
}));
